// Total introduction order over curriculum atoms — the sequence the ladder spine descends
// through (spec 2026-06-19). Pure leaf (no engine/render imports). The texture order is
// dedicated here and is INDEPENDENT of difficulty-tables TEXTURE_RANK (which stays calibrated
// for grading): here alberti sits strictly after broken.
import { isChordAtom } from "./chord-atoms";
import { SKILLS } from "./skills";

// Strict texture sequence (NOT difficulty's TEXTURE_RANK): blocked < broken < alberti < arpeggio.
const TEXTURE_ORDER: Record<string, number> = {
  "five-finger": 0,
  "blocked-chords": 1,
  "broken-chords": 2,
  "alberti-bass": 3,
  arpeggios: 4,
};

const INTRO_GRADE = new Map(SKILLS.map((s) => [s.id, s.introGrade]));
const PREREQ = new Map(SKILLS.map((s) => [s.id, s.prereqs]));

/** Prereq-closure depth of an atom in the skills DAG — the "tier" for choice-vs-sequence:
 *  same-tier atoms are peers (offered as choices), different tiers are sequenced. Chord atoms
 *  (derived, not skills) have no prereqs → tier 0. */
export function atomTier(atom: string): number {
  const prereqs = PREREQ.get(atom);
  if (!prereqs || prereqs.length === 0) return 0;
  return 1 + Math.max(...prereqs.map(atomTier));
}

// Coarse band, introduced in this order: five-finger, then the chord vocabulary (the first
// chord brings blocked-chords for free), then the harder textures (broken → alberti →
// arpeggio) revisited over the known chords, then shapes/progressions/scales. Putting the
// textures AFTER the chords is what stacks a tune's arrangements vertically (drone → block →
// broken → alberti, each deeper than the last) rather than collapsing them to one level.
function band(atom: string): number {
  if (atom === "five-finger") return 0;
  if (isChordAtom(atom)) return 1; // the chord vocabulary (first chord grants blocked-chords)
  if (atom in TEXTURE_ORDER) return 2; // broken/alberti/arpeggio, revisited over the chords
  return 3; // shapes, progressions, hand-independence, scales
}

interface OrderOpts {
  chordRank: Map<string, number>; // chord atom → corpus-frequency rank (0 = most common)
  override?: string[]; // atoms forced to the front, in listed order
}

/** Map each atom to a distinct, deterministic order index. Override entries sort first (in
 *  list order); the rest sort by band → texture order → chord rank / introGrade → id. */
export function introductionOrder(atoms: readonly string[], opts: OrderOpts): Map<string, number> {
  const uniq = [...new Set(atoms)];
  const override = opts.override ?? [];
  const ovIndex = new Map(override.map((a, i) => [a, i]));
  const pad = (n: number): string => String(n).padStart(5, "0");
  // A single sortable key: override-flag, override-pos, band, texture order, chord/intro rank,
  // then the atom id. Lexicographic compare over zero-padded fields is the total order.
  const sortKey = (a: string): string =>
    [
      pad(ovIndex.has(a) ? 0 : 1),
      pad(ovIndex.get(a) ?? 0),
      pad(band(a)),
      pad(TEXTURE_ORDER[a] ?? 0),
      pad(isChordAtom(a) ? (opts.chordRank.get(a) ?? 999) : (INTRO_GRADE.get(a) ?? 99)),
      a,
    ].join("|");
  const sorted = uniq.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
  return new Map(sorted.map((a, i) => [a, i]));
}
